import * as React from 'react'

import { InfoIcon, XIcon } from 'lucide-react'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from '~/client/src/shared/components/shadcn-ui/drawer'
import Localization from '~/client/src/shared/localization/LocalizationManager'

interface IProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  showLocationTool?: boolean
}

interface IGestureRow {
  label: string
  gesture: string
}

interface IGestureGroup {
  title: string
  rows: IGestureRow[]
}

const buildGestureGroups = (showLocationTool: boolean): IGestureGroup[] => {
  const t = Localization.translator
  return [
    {
      title: t.select,
      rows: [
        { label: t.gestureSelectMarkup, gesture: t.gestureTap },
        { label: t.gestureEditPoints, gesture: t.gestureTapAgain },
        { label: t.gestureDeselect, gesture: t.gestureTapMap },
      ],
    },
    {
      title: t.gesturePointsGroup,
      rows: [
        { label: t.gestureMovePoint, gesture: t.gestureDrag },
        { label: t.gestureAddPoint, gesture: t.gestureTapMidpoint },
        { label: t.gestureRemovePoint, gesture: t.gestureLongPress },
        { label: t.gestureClosePolygon, gesture: t.gestureTapFirstPoint },
      ],
    },
    {
      title: t.draw,
      rows: [
        { label: t.gestureRectangleCircle, gesture: t.gestureTapOrDrag },
        { label: t.gestureLineArrow, gesture: t.gestureDragOrTapEnds },
        { label: t.gesturePolygonPolyline, gesture: t.gestureTapPointsClose },
        { label: t.gesturePenHighlighterEraser, gesture: t.gestureDrag },
      ],
    },
    {
      title: t.gestureTextGroup,
      rows: [
        { label: t.gestureAddText, gesture: t.gestureTap },
        { label: t.gestureEditText, gesture: t.gestureDoubleTap },
        {
          label: t.gestureMoveResizeRotate,
          gesture: t.gestureDragBodyCornerPin,
        },
      ],
    },
    ...(showLocationTool
      ? [
          {
            title: t.location,
            rows: [
              {
                label: t.gestureRepositionPin,
                gesture: t.gestureTapMoveThenDrag,
              },
            ],
          },
        ]
      : []),
    {
      title: t.gestureMapGroup,
      rows: [
        { label: t.gesturePan, gesture: t.gestureDrag },
        { label: t.gestureZoom, gesture: t.gesturePinch },
        { label: t.gestureRotateTilt, gesture: t.gestureTwoFingerDrag },
      ],
    },
  ]
}

// Touch counterpart to the desktop shortcuts legend — gestures, not keystrokes.
const MarkupGesturesLegend: React.FC<IProps> = ({
  open,
  onOpenChange,
  showLocationTool,
}) => (
  <Drawer
    open={open}
    onOpenChange={onOpenChange}
    // vaul's body position-lock collapses the map's AutoSizer (STX1-443),
    // tearing down the map — its canvas then crashes drawing on reopen.
    noBodyStyles
  >
    <DrawerTrigger asChild>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={Localization.translator.gestures}
        aria-pressed={open}
        className={
          open
            ? 'tw:bg-primary/8 tw:text-primary tw:hover:bg-primary/8'
            : 'tw:text-foreground'
        }
      >
        <InfoIcon aria-hidden="true" />
      </Button>
    </DrawerTrigger>
    <DrawerContent>
      <div className="tw:flex tw:items-center tw:gap-1 tw:px-1 tw:pt-1">
        <DrawerClose asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={Localization.translator.close}
          >
            <XIcon aria-hidden="true" />
          </Button>
        </DrawerClose>
        <DrawerTitle className="tw:flex-1 tw:text-center">
          {Localization.translator.gestures}
        </DrawerTitle>
        <span aria-hidden className="tw:size-9 tw:shrink-0" />
      </div>
      <DrawerDescription className="tw:sr-only">
        {Localization.translator.gestures}
      </DrawerDescription>
      <div className="tw:flex tw:flex-col tw:gap-4 tw:overflow-y-auto tw:px-3 tw:pb-3">
        {buildGestureGroups(!!showLocationTool).map(group => (
          <div key={group.title} className="tw:flex tw:flex-col tw:gap-1">
            <div className="tw:text-label-sm tw:text-muted-foreground tw:uppercase">
              {group.title}
            </div>
            {group.rows.map(row => (
              <div
                key={row.label}
                className="tw:flex tw:items-center tw:justify-between tw:gap-3"
              >
                <span className="tw:text-sm tw:text-foreground">
                  {row.label}
                </span>
                <span className="tw:shrink-0 tw:text-right tw:text-sm tw:text-muted-foreground">
                  {row.gesture}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </DrawerContent>
  </Drawer>
)

export default MarkupGesturesLegend
